import { closest, isEmail } from '@jotforminc/utils';
import { FORM_TEXTS } from '@jotforminc/constants';

import VerificationPrompterBox from './verificationPrompterBox';
import {
  isFilled, isEmpty, isEqual
} from './validationHelper';
import {
  addClass, removeClass, forEach, fuzzySearch, createInput
} from './utils';
import { MOST_USED_PROVIDERS } from './constants';
import Storage from './storage';

const tokenTTL = 60 * 1000; // 1 minute in milliseconds
const baseLocalStorageKey = 'jf_email_verification_code';

class Email {
  /* INIT FUNCTIONS */
  constructor(params) {
    const { cardContainer } = params;
    this.question = params.question;
    this.formID = params.formID;
    this.showTooltip = params.showTooltip;
    this.removeTooltip = params.removeTooltip;
    this.layoutElements = { cardContainer };
    this.getLayoutElements = this.getLayoutElements.bind(this);
    this.getLayoutElements(Email.defaultSelectors);

    this.onTypingFinished = this.onTypingFinished.bind(this);
    this.handleInputEvent = this.handleInputEvent.bind(this);
    this.handleConfirmationInputEvent = this.handleConfirmationInputEvent.bind(this);
    this.resetTimer = this.resetTimer.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.validateField = this.validateField.bind(this);
    this.onVerificationFail = this.onVerificationFail.bind(this);
    this.setVerificationButtonStatus = this.setVerificationButtonStatus.bind(this);
    this.saveVerificationCodeInfo = this.saveVerificationCodeInfo.bind(this);
    this.loadVerificationCodeInfo = this.loadVerificationCodeInfo.bind(this);
    this.deleteVerificationCodeInfo = this.deleteVerificationCodeInfo.bind(this);
    this.clearVerificationCodeInfo = this.clearVerificationCodeInfo.bind(this);
    this.isVerificationCodeFresh = this.isVerificationCodeFresh.bind(this);
    this.startVerificationMode = this.startVerificationMode.bind(this);

    if (this.hasVerificationArea) { // This means user is using domainCheck or verificationCode
      this.initVerificationArea = this.initVerificationArea.bind(this);
      this.initVerificationArea();
      setTimeout(() => {
        if (window.JotForm.isEditMode()) {
          this.finishVerificationStep();
        }
      }, 800);
    }

    const { primaryInput, confirmationInput } = this.layoutElements;

    primaryInput.addEventListener('input', this.handleInputEvent);
    primaryInput.addEventListener('keydown', this.handleKeyDown);

    this.setMode = this.setMode.bind(this);
    this.setVerificationStatus = this.setVerificationStatus.bind(this);

    this.state = {
      mode: 'questionMode'
    };

    primaryInput.overridenValidateInput = this.validateField;
    if (this.hasValidation('Email_Confirm')) {
      confirmationInput.addEventListener('input', this.handleConfirmationInputEvent);
      confirmationInput.overridenValidateInput = this.validateField;
    }
    this.setMode(this.state.mode);

    this.useTooltip = true;

    if (this.acceptedDomains) {
      this.moveSuggestionDropdownToBody();
    }

    this.storageEngine = new Storage({ adapter: 'localstorage' });
  }

  getLayoutElements(selectors) {
    forEach(Object.keys(selectors), key => {
      const item = selectors[key];
      const container = this.layoutElements[item.parent];
      this.layoutElements[key] = container ? container.querySelector(item.selector) : undefined;
    });
  }

  initVerificationArea() {
    const { emailVerificationArea, sendVerificationCodeButton } = this.layoutElements;
    if (new URLSearchParams(window.location.search).get('emailVerifiedInAgent') === '1') {
      const { questionContainer } = this.layoutElements;
      addClass(questionContainer, 'isVerifiedEmail');
      this.isEmailVerified = true;
    }
    this.handleSendVerificationCodeButton = this.handleSendVerificationCodeButton.bind(this);
    this.cancelVerificationStep = this.cancelVerificationStep.bind(this);
    this.finishVerificationStep = this.finishVerificationStep.bind(this);
    this.checkVerificationCode = this.checkVerificationCode.bind(this);
    this.resendVerificationCode = this.resendVerificationCode.bind(this);
    this.onVerificationSuccess = this.onVerificationSuccess.bind(this);

    this.verificationPrompterBox = new VerificationPrompterBox({
      onFinished: this.checkVerificationCode,
      onSuccess: this.onVerificationSuccess,
      onFail: this.onVerificationFail,
      onResendCode: this.resendVerificationCode,
      onChangeEmail: () => {
        this._token = null;
        this.setMode('questionMode');
      },
      container: emailVerificationArea
    });

    sendVerificationCodeButton.addEventListener('click', this.handleSendVerificationCodeButton);
  }

  /* GETTERS */
  get validations() {
    // Get all validations for all inputs in component
    const { primaryInput, confirmationInput } = this.layoutElements;
    const primaryInputValidations = global.JotForm.getInputValidations(primaryInput);
    const confirmationInputValidations = confirmationInput ? global.JotForm.getInputValidations(confirmationInput) : [];
    return [...primaryInputValidations, ...confirmationInputValidations].filter((value, index, self) => self.indexOf(value) === index); // distinct
  }

  get token() {
    if (!this._token) {
      const { primaryInput } = this.layoutElements;
      if (this.isVerificationCodeFresh()) {
        this._token = this.loadVerificationCodeInfo().token;
      } else {
        this._token = this.generateToken(global.CardForm.formID, primaryInput.value);
      }
    }
    return this._token;
  }

  get localStorageKey() {
    const { primaryInput } = this.layoutElements;
    return `${baseLocalStorageKey}_${this.formID}_${primaryInput.value}`;
  }

  get emailDomain() {
    if (this.hasAtCharacter) {
      return this.layoutElements.primaryInput.value.split('@')[1];
    }
    return false;
  }

  get emailUsername() {
    if (this.hasAtCharacter) {
      return this.layoutElements.primaryInput.value.split('@')[0];
    }
    return false;
  }

  get hasDotCharacter() {
    return this.emailDomain ? this.emailDomain.indexOf('.') > -1 : false;
  }

  get hasVerificationArea() {
    return this.hasValidation('verificationCode') || this.hasValidation('domainCheck');
  }

  get hasAtCharacter() {
    const { primaryInput } = this.layoutElements;
    return primaryInput.value.indexOf('@') > -1;
  }

  get freeEmailAddresses() {
    return global.JotForm.freeEmailAddresses;
  }

  get isFreeDomain() {
    if (this.emailDomain) {
      const currentDomainName = this.emailDomain;
      const isFreeDomain = this.freeEmailAddresses.filter(domain => `@${currentDomainName}`.indexOf(`@${domain}`) > -1).length > 0;
      return isFreeDomain;
    }
    return false;
  }

  get isAcceptedDomain() {
    return this.acceptedDomains.indexOf(this.emailDomain.toString().toLowerCase()) > -1;
  }

  get hasError() {
    const { primaryInput, confirmationInput } = this.layoutElements;
    return primaryInput.errored || (confirmationInput && confirmationInput.errored);
  }

  translate() {
    const formTranslation = global.FormTranslation;
    if (!formTranslation) return;
    if (this.verificationPrompterBox && this.verificationPrompterBox.translate) this.verificationPrompterBox.translate(formTranslation);
  }

  get isEmailVerificationButtonActive() {
    if (!this.hasVerificationArea) { return; }
    const { sendVerificationCodeButton } = this.layoutElements;
    return !sendVerificationCodeButton.disabled;
  }

  get hasSuggestion() {
    // will be updated
    return false;
  }

  get providerList() {
    return MOST_USED_PROVIDERS;
  }

  get acceptedDomains() {
    // returns list if field has pre-defined domain values - returns false otherwise
    let domainList = this.layoutElements.primaryInput.getAttribute('data-accepted-domains');
    if (domainList && domainList !== '') {
      domainList = domainList.strip().split('|').filter(domain => { return domain !== ''; }).map(domain => { return domain.toLowerCase(); });
      return domainList.length ? domainList : false;
    }
    return false;
  }

  get showSuggestions() {
    return this.emailUsername.length > 1 && !this.acceptedDomains && this.useTooltip;
  }

  get suggestionList() {
    /* suggest a correction for the misspelled domain */
    /* or */
    /* suggest a list of providers for dropdown */
    let suggestionList = this.acceptedDomains ? this.acceptedDomains : this.providerList;
    if (this.hasValidation('disallowFree')) {
      // if free addresses are disallowed, remove them from suggestions
      suggestionList = suggestionList.filter(domain => {
        const emailTLD = domain.split('.')[0];
        const checkList = [emailTLD + '.', domain];

        return !checkList.some(item => this.freeEmailAddresses.includes(item));
      });
    }
    return suggestionList;
  }

  /* END GETTERS */

  /* SETTERS */
  setMode(newMode) {
    const { questionContainer } = this.layoutElements;
    // const currentMode = this.state.mode;
    const allModes = ['questionMode', 'verificationMode', 'loadingMode'];
    forEach(allModes, mode => {
      removeClass(questionContainer, mode);
    });
    addClass(questionContainer, newMode);

    this.state = { ...this.state, mode: newMode };
  }

  setVerificationStatus(status) {
    this.isEmailVerified = status;
    const { questionContainer, primaryInput } = this.layoutElements;
    if (status) {
      addClass(questionContainer, 'isVerifiedEmail');
    } else {
      removeClass(questionContainer, 'isVerifiedEmail');
    }
    this.validateField(primaryInput);
  }

  generateToken(formId, emailAddress) {
    const formIdStr = formId.toString();
    const temporal = new Date().getTime().toString().slice(-4);
    const formIdLastNumber = formIdStr.slice(-1) / 1;
    const tokenCode = emailAddress.charCodeAt(0) + formIdLastNumber;
    const newToken = `JF-${temporal}-${tokenCode}`;
    this.saveVerificationCodeInfo(newToken);
    return newToken;
  }

  isVerificationCodeFresh() {
    const verificationCodeInfo = this.loadVerificationCodeInfo();
    if (!verificationCodeInfo) {
      return false;
    }
    const { generatedAt } = verificationCodeInfo;

    const nowTimestamp = new Date().getTime();
    if (nowTimestamp - generatedAt > tokenTTL) {
      this.deleteVerificationCodeInfo();
      return false;
    }
    return true;
  }

  saveVerificationCodeInfo(token) {
    const storageData = JSON.stringify({
      token: token, generatedAt: new Date().getTime()
    });
    this.storageEngine.set(this.localStorageKey, storageData);
  }

  loadVerificationCodeInfo() {
    let verificationCodeInfo = null;

    try {
      verificationCodeInfo = JSON.parse(this.storageEngine.get(this.localStorageKey));
    } catch (e) {
      console.error(e);
      return false;
    }
    return verificationCodeInfo;
  }

  deleteVerificationCodeInfo() {
    this.storageEngine.delete(this.localStorageKey);
  }

  clearVerificationCodeInfo() {
    this.storageEngine.deleteAll(`^${baseLocalStorageKey}_${this.formID}_`);
  }

  hasValidation(validationName) {
    return this.validations.indexOf(validationName) > -1;
  }

  closeTooltip() {
    this.removeTooltip(this.layoutElements.primaryInput);
  }

  addTooltip(misspelledDomain) {
    const { primaryInput } = this.layoutElements;
    const suggestedEmail = `${this.emailUsername}@${misspelledDomain}`;
    const tooltipOptions = {
      message: `Did you mean |${suggestedEmail}|?`,
      callback: () => {
        primaryInput.value = suggestedEmail;
      },
      onClose: () => {
        this.useTooltip = false;
      }
    };
    this.showTooltip(primaryInput, tooltipOptions);
  }

  moveSuggestionDropdownToBody() {
    const { suggestionDropdown } = this.layoutElements;
    suggestionDropdown.parentNode.removeChild(suggestionDropdown);
    suggestionDropdown.style.position = 'fixed'; // eslint-disable-line
    document.body.appendChild(suggestionDropdown);
  }

  handleDropdownPosition() {
    const { suggestionDropdown, primaryInput } = this.layoutElements;
    const inputRect = primaryInput.getBoundingClientRect();
    suggestionDropdown.style.top = inputRect.bottom;
    suggestionDropdown.style.left = inputRect.left;
    suggestionDropdown.style.width = inputRect.width;
  }

  handleDropdownEvents() {
    if (!this.acceptedDomains) { return; }
    const domainOptions = this.handleSuggestionOptions();
    const { suggestionDropdown, primaryInput } = this.layoutElements;
    const ul = suggestionDropdown.querySelector('.jfDropdown-optionList');
    // clear the list first
    while (ul.hasChildNodes()) { ul.removeChild(ul.firstChild); }

    // update it's position
    this.handleDropdownPosition();
    document.addEventListener('click', e => {
      if (!closest(e.target, '.jsForEmailSuggestion')) {
        this.closeDropdown();
      }
    });

    forEach(domainOptions, (option, index) => {
      const item = document.createElement('li');
      const itemText = `${this.emailUsername}@${option}`;
      item.setAttribute('data-value', option);
      item.setAttribute('key', `co${index}`);
      item.setAttribute('class', 'jfDropdown-optionListItem');
      item.innerText = itemText; //eslint-disable-line
      ul.appendChild(item);

      item.addEventListener('click', e => {
        primaryInput.value = e.target.innerHTML;
        e.preventDefault();
        this.closeDropdown();
      });
    });
  }

  handleFuzzySearch() {
    if (!this.emailDomain || !this.emailUsername) { return []; }
    const searchItem = this.emailDomain;
    const providerList = this.suggestionList;
    const suggestions = fuzzySearch(searchItem, providerList);
    return suggestions;
  }

  handleTooltip() {
    const suggestions = this.handleFuzzySearch();
    if (suggestions.length && this.suggestionList.indexOf(this.emailDomain) === -1) {
      // show only one suggestion if misspelled item is in our list
      this.addTooltip(suggestions[0]);
    } else {
      this.closeTooltip();
    }
  }

  handleSuggestionOptions() {
    const suggestions = this.handleFuzzySearch();
    return suggestions.length ? suggestions : this.suggestionList;
  }

  onTypingFinished() {
    const { primaryInput, confirmationInput } = this.layoutElements;
    const confirmValue = this.hasValidation('Email_Confirm') ? confirmationInput.value : primaryInput.value;
    if (this.hasAtCharacter && this.showSuggestions) {
      // suggest a did you mean value
      this.handleTooltip();
    }

    if (this.acceptedDomains && this.isAcceptedDomain) {
      // close the suggestion dropdown - if domain is accepted
      this.closeDropdown();
    }

    if (isEmail(primaryInput.value) && confirmValue === primaryInput.value) {
      if (this.hasError) {
        return this.validateField(confirmationInput || primaryInput);
      }
      this.setVerificationButtonStatus(true);
    } else if (this.isEmailVerificationButtonActive) {
      this.setVerificationButtonStatus(false);
    }
  }

  cancelVerificationStep() {
    this._token = null;
    this.setMode('questionMode');
  }

  finishVerificationStep() {
    this.setVerificationStatus(true);
    this.setMode('questionMode');
    this.verificationPrompterBox.clear(true);
  }

  startVerificationMode() {
    this.setMode('verificationMode');
    const { primaryInput } = this.layoutElements;
    this.verificationPrompterBox.setEmailAddress(primaryInput.value);
    this.verificationPrompterBox.layoutElements.input.focus();
  }

  handleSendVerificationCodeButton() {
    this.setMode('loadingMode');

    if (this.isVerificationCodeFresh()) {
      this.startVerificationMode();
      return;
    }

    const { primaryInput } = this.layoutElements;

    const formData = JSON.stringify({
      formID: global.CardForm.formID,
      emailAddress: primaryInput.value,
      token: this.token
    });

    fetch(`${global.CardForm.apiURL}/sendConfirmationCodeEmail`, { method: 'POST', body: formData })
      .then(responseObject => {
        responseObject.json().then(data => {
          if (data.responseCode === 200) {
            this.startVerificationMode();
          } else {
            this.setMode('questionMode');
            console.error(data.message);
          }
        });
      })
      .catch(
        error => {
          error.json().then(err => {
            this.setMode('questionMode');
            console.error(err);
          });
        }
      );
  }

  setVerificationButtonStatus(status) {
    if (!this.hasVerificationArea) { return; }
    const { sendVerificationCodeButton, sendVerificationCodeButtonWrapper } = this.layoutElements;
    if (status) {
      removeClass(sendVerificationCodeButtonWrapper, 'isPassive');
      sendVerificationCodeButton.disabled = false;
    } else {
      addClass(sendVerificationCodeButtonWrapper, 'isPassive');
      sendVerificationCodeButton.disabled = true;
    }
  }

  resendVerificationCode() {
    this._token = null;
    this.deleteVerificationCodeInfo();
    this.handleSendVerificationCodeButton();
  }

  checkVerificationCode(code, verificationCallback) {
    const { primaryInput } = this.layoutElements;

    const formData = JSON.stringify({
      formID: global.CardForm.formID,
      emailAddress: primaryInput.value,
      token: this.token,
      confirmationCode: code
    });

    fetch(`${global.CardForm.apiURL}/verifyConfirmationCode`, { method: 'POST', body: formData })
      .then(responseObject => {
        responseObject.json().then(data => {
          if (data.responseCode === 200) {
            this.addHiddenField(this.token, code);
            this._token = null;
            this.clearVerificationCodeInfo();
            verificationCallback(true);
          } else {
            verificationCallback(false);
            console.error(data.message);
          }
        });
      })
      .catch(
        error => {
          error.json().then(err => {
            verificationCallback(false);
            console.error(err);
          });
        }
      );
  }

  // send token and confirmation code to verify email in backend during submission
  addHiddenField(token, confirmationCode) {
    const name = `emailVerificationToken_${this.question.id}`;
    const value = JSON.stringify({ token, confirmationCode });
    const input = document.querySelector(`[name='${name}']`);
    if (input) {
      input.value = value;
    } else {
      const form = document.querySelector('.jotform-form');
      if (form) {
        form.appendChild(createInput({ type: 'hidden', name, value }));
      }
    }
  }

  onVerificationSuccess() {
    this.isEmailVerified = true; // shortcut fixes #1330989
    setTimeout(this.finishVerificationStep, 800); // Wait for icon animation to finish.
  }

  onVerificationFail() {
    // console.log('on verification fail');
    // this.validateField(this.layoutElements.primaryInput);
  }

  closeDropdown() {
    removeClass(this.layoutElements.suggestionDropdown, 'isOpen');
  }

  openDropdown() {
    addClass(this.layoutElements.suggestionDropdown, 'isOpen');
  }

  resetTimer() {
    if (typeof this.typingTimeout !== 'undefined') { clearTimeout(this.typingTimeout); }
    this.typingTimeout = setTimeout(this.onTypingFinished, 750);
  }

  handleInputEvent() {
    if (this.isEmailVerified) {
      this.setVerificationStatus(false);
    }

    this.resetTimer();

    if (this.hasAtCharacter && this.acceptedDomains) {
      // call suggestion and correction methods here
      this.openDropdown();
      this.handleDropdownEvents();
      if (this.isAcceptedDomain) { this.closeDropdown(); }
    } else {
      this.closeDropdown();
      this.closeTooltip();
    }
  }

  handleConfirmationInputEvent() {
    this.resetTimer();
  }

  handleKeyDown(event) {
    const { key, keyCode } = event;
    if (keyCode === 13 || key === 'Enter') {
      // console.log('enter pressed now?');
    }
  }

  /* Validations */

  // patch for input.validateInput
  validateField(input, deep, _dontShowMessage) {
    if (this.state.mode === 'loadingMode') { return; }
    const dontShowMessage = _dontShowMessage;
    let verificationButtonStatus = false;
    const { primaryInput, confirmationInput } = this.layoutElements;
    const primaryInputValue = primaryInput.value;
    const confirmationValue = this.hasValidation('Email_Confirm') ? confirmationInput.value : primaryInputValue;
    let errorDetails = { errorState: false, errorMessage: '' };

    const isValidEmail = isEmail(primaryInputValue);
    if (this.hasValidation('required') && isEmpty(primaryInputValue)) { // Required Check
      errorDetails = { errorState: true, errorMessage: global.JotForm.texts.required || FORM_TEXTS.required };
    } else if (isFilled(primaryInputValue) && !isValidEmail) { // Is Valid Email check
      errorDetails = { errorState: true, errorMessage: global.JotForm.texts.email || FORM_TEXTS.email };
    } else if (this.hasValidation('disallowFree') && isValidEmail && this.isFreeDomain) { // isFreeDomain
      errorDetails = { errorState: true, errorMessage: global.JotForm.texts.freeEmailError || FORM_TEXTS.freeEmailError };
    } else if (isValidEmail && this.hasValidation('allowedDomains') && !this.isAcceptedDomain) {
      errorDetails = { errorState: true, errorMessage: 'This domain is not allowed.' };
    } else if (this.hasValidation('Email_Confirm') && input === confirmationInput && !isEqual(primaryInputValue, confirmationValue)) {
      errorDetails = { errorState: true, errorMessage: global.JotForm.texts.confirmEmail || FORM_TEXTS.confirmEmail, targetInput: confirmationInput };
    } else if (isValidEmail && this.hasValidation('verificationCode') && !this.isEmailVerified) {
      verificationButtonStatus = true;
      errorDetails = { errorState: true, errorMessage: global.JotForm.texts.validateEmail || FORM_TEXTS.validateEmail };
    }

    const { errorState, errorMessage, targetInput } = errorDetails;
    // error
    if (errorState) {
      this.setVerificationButtonStatus(verificationButtonStatus);
      return global.JotForm.errored((targetInput || primaryInput), errorMessage, dontShowMessage);
    }
    this.setVerificationButtonStatus(isValidEmail);
    return global.JotForm.corrected((targetInput || primaryInput));
  }
}

Email.defaultSelectors = {
  primaryInput: { parent: 'cardContainer', selector: '.jfField[data-type=email] input.forEmail' },
  confirmationInput: { parent: 'cardContainer', selector: '.jfField[data-type=emailConfirmation] input.forEmail' },
  fieldContainer: { parent: 'cardContainer', selector: '.jfField' },
  questionContainer: { parent: 'cardContainer', selector: '.jfQuestion-fields' },
  suggestionDropdown: { parent: 'cardContainer', selector: '.jsForEmailSuggestion' },
  sendVerificationCodeButtonWrapper: { parent: 'cardContainer', selector: '.js-emailVerificationButtonWrapper' },
  sendVerificationCodeButton: { parent: 'sendVerificationCodeButtonWrapper', selector: '.js-emailVerificationButton' },
  emailVerificationArea: { parent: 'cardContainer', selector: '.jfEmailVerify-wrapper.js-emailVerificationArea' }
};

export default Email;
